import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@perxis-auto/ui-kit/lib/components/Tabs';

import formatEquipmentGroups from '@/blocks/constructor/Equipments/helpers/formatEquipmentGroups';
import formatSpecificationGroups from '@/blocks/constructor/Equipments/helpers/formatSpecificationGroups';
import useCarData from '@/features/cars/hooks/useCarData';
import useVehiclesSpecificationGroups from '@/features/dictionaries/hooks/useVehiclesSpecificationGroups';
import useVehiclesSpecifications from '@/features/dictionaries/hooks/useVehiclesSpecifications';
import { useAppDispatch } from '@/features/hooks';
import { rootActions } from '@/features/store';
import useEquipmentsExpanded from '@/hooks/useEquipmentsExpanded';
import { ICarOption } from '@/interfaces/cars.interfaces';

import EquipmentItem, { EquipmentItemOptionType } from './components/EquipmentItem';
import { EquipmentsProps } from './Equipments.types';

import styles from './Equipments.module.scss';

/** Предустановленный список, отображаемых разделов. */
const EQUIPMENT_SECTIONS = [
  {
    path: 'baseOptions',
    title: 'equipments.base',
  },
  {
    path: 'extraOptions',
    title: 'equipments.additional',
  },
  {
    path: 'specifications',
    title: 'equipments.specification',
  },
] as const;

/**
 * Вспомогательная функция, возвращающая идентификаторы раскрытых групп для конкретного раздела
 * @param groups массив групп
 * @param expandedIds массив идентификаторов всех раскрытых групп
 */
function getActiveGroupIds(groups: Array<{ id: string }>, expandedIds: Array<string>) {
  return groups.filter(group => expandedIds.includes(group.id)).map(group => group.id);
}

/**
 * Реализация блока оборудования.
 * В данной реализации данные для оборудования берутся из IExtendedCarItem.data.options.
 * Информация для технических характеристик берется из IExtendedCarItem.data.specificationValues.
 */
function Equipments(props: EquipmentsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: extendedCarItem } = useCarData();

  const carId = extendedCarItem?.id;

  const { items: specificationsDictionary } = useVehiclesSpecifications();
  const { items: specificationsGroupsDictionary } = useVehiclesSpecificationGroups();
  const expandedGroupIds = useEquipmentsExpanded(carId);

  const onGroupExpanded = useCallback(
    (expandedIds: Array<string>, groups: Array<EquipmentItemOptionType>) => {
      if (!carId) {
        return;
      }

      // Собираем идентификаторы, которые нужно исключить из стейта.
      const closedGroups = groups
        .filter(group => !expandedIds.includes(group.id))
        .map(group => group.id);

      // Исключаем закрытые группы из стейта.
      const filteredEquipmentGroupIds = expandedGroupIds.filter(
        id => !closedGroups.includes(id),
      );

      // Добавляем новые, открытые группы в стейт.
      const equipmentGroupSet = new Set(filteredEquipmentGroupIds);
      expandedIds.forEach(groupId => equipmentGroupSet.add(groupId));

      // Обновляем стейт открытых групп.
      dispatch(
        rootActions.equipmentsExpanded.upsertEquipmentsExpanded({
          id: carId,
          groups: Array.from(equipmentGroupSet),
        }),
      );
    },
    [carId, dispatch, expandedGroupIds],
  );

  /** Раздел "Технические характеристики" */
  const specificationTab = useMemo(() => {
    if (
      !extendedCarItem?.data.specificationsValues?.length ||
      !specificationsDictionary?.length ||
      !specificationsGroupsDictionary?.length
    ) {
      return null;
    }

    const specificationGroups = formatSpecificationGroups(
      extendedCarItem.data.specificationsValues,
      specificationsDictionary,
      specificationsGroupsDictionary,
      t,
    );

    const activeGroups = getActiveGroupIds(specificationGroups, expandedGroupIds);

    return {
      key: EQUIPMENT_SECTIONS[2].path,
      label: t(EQUIPMENT_SECTIONS[2].title),
      children: (
        <EquipmentItem
          groups={specificationGroups}
          activeGroups={activeGroups}
          onActiveGroupChange={ids => onGroupExpanded(ids, specificationGroups)}
        />
      ),
    };
  }, [
    expandedGroupIds,
    extendedCarItem?.data.specificationsValues,
    onGroupExpanded,
    specificationsDictionary,
    specificationsGroupsDictionary,
    t,
  ]);

  /** Разделы оборудования (основного и дополнительного) */
  const equipmentTabs = useMemo(() => {
    if (!extendedCarItem?.data.options) {
      return [];
    }

    const baseOptions: Array<ICarOption> = [];
    const extraOptions: Array<ICarOption> = [];

    extendedCarItem.data.options.forEach(option => {
      if (option.isBase) {
        baseOptions.push(option);
        return;
      }

      extraOptions.push(option);
    });

    const baseOptionGroups = formatEquipmentGroups(baseOptions);
    const extraOptionGroups = formatEquipmentGroups(extraOptions);

    const baseActiveGroups = getActiveGroupIds(baseOptionGroups, expandedGroupIds);
    const extraActiveGroups = getActiveGroupIds(extraOptionGroups, expandedGroupIds);

    const baseTab = baseOptionGroups.length
      ? {
          key: EQUIPMENT_SECTIONS[0].path,
          label: t(EQUIPMENT_SECTIONS[0].title),
          children: (
            <EquipmentItem
              groups={baseOptionGroups}
              activeGroups={baseActiveGroups}
              onActiveGroupChange={ids => onGroupExpanded(ids, baseOptionGroups)}
            />
          ),
        }
      : null;

    const extraTab = extraOptionGroups.length
      ? {
          key: EQUIPMENT_SECTIONS[1].path,
          label: t(EQUIPMENT_SECTIONS[1].title),
          children: (
            <EquipmentItem
              groups={extraOptionGroups}
              activeGroups={extraActiveGroups}
              onActiveGroupChange={ids => onGroupExpanded(ids, extraOptionGroups)}
            />
          ),
        }
      : null;

    const tabs = [];

    if (baseTab) {
      tabs.push(baseTab);
    }

    if (extraTab) {
      tabs.push(extraTab);
    }

    return tabs;
  }, [expandedGroupIds, extendedCarItem?.data.options, onGroupExpanded, t]);

  // Формируем список вкладок для отображения
  const tabs = useMemo(() => {
    return specificationTab ? [...equipmentTabs, specificationTab] : equipmentTabs;
  }, [specificationTab, equipmentTabs]);

  const isHidden = !extendedCarItem || !extendedCarItem.data || !tabs.length;

  if (isHidden) {
    return null;
  }

  return (
    <div className={styles.equipments}>
      <Tabs destroyInactiveTabPane items={tabs} />
    </div>
  );
}

export default Equipments;
